import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal } from '@/shared/components/molecules';
import { Home, Tag as TagIcon, Trash2, Plus, Pencil, Search, X } from 'lucide-react';
import CustomModal from '@/shared/components/atoms/CustomModal/CustomModal';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { tagApi } from '../api/tagApi';
import { Tag } from '../types';

const addTagSchema = z.object({
    name: z.string().min(2, 'Name must be at least 2 characters'),
    description: z.string().min(5, 'Description must be at least 5 characters'),
});

type AddTagFormData = z.infer<typeof addTagSchema>;

export const Tags = () => {
    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    const [tags, setTags] = useState<Tag[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);

    // Search and Sort states
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
    const [tagToEdit, setTagToEdit] = useState<Tag | null>(null);
    const { showToast } = useToastStore();

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [search]);

    const { register, handleSubmit, reset, formState: { errors } } = useForm<AddTagFormData>({
        resolver: zodResolver(addTagSchema),
    });

    const fetchTags = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        const response = await tagApi.getTags(schoolSlug, {
            page,
            page_size: pageSize,
            search: debouncedSearch.trim() || undefined,
            sort_by: sortBy,
            sort_order: sortOrder
        });
        if (response.success && response.data) {
            const data = response.data as any;
            if (Array.isArray(data)) {
                setTags(data);
                setTotalCount(data.length);
                setTotalPages(1);
                setHasNext(false);
                setHasPrevious(false);
            } else {
                setTags(data.items || []);
                setTotalCount(data.total_count || 0);
                setTotalPages(data.total_pages || 1);
                setHasNext(data.has_next || false);
                setHasPrevious(data.has_previous || false);
            }
        } else {
            showToast(response.error || 'Failed to fetch tags', 'error');
        }
        setIsLoading(false);
    }, [schoolSlug, page, pageSize, debouncedSearch, sortBy, sortOrder, showToast]);

    useEffect(() => {
        fetchTags();
    }, [fetchTags]);

    const handleDeleteClick = (tag: Tag) => {
        setTagToDelete(tag);
        setIsDeleteModalOpen(true);
    };

    const handleEditClick = (tag: Tag) => {
        setTagToEdit(tag);
        reset({ name: tag.name, description: tag.description || '' });
        setIsAddModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (tagToDelete && schoolSlug) {
            setIsDeleting(true);
            const response = await tagApi.deleteTag(schoolSlug, tagToDelete.id);
            if (response.success) {
                setTags(prev => prev.filter(t => t.id !== tagToDelete.id));
                showToast('Tag deleted successfully', 'success');
            } else {
                showToast(response.error || 'Failed to delete tag', 'error');
            }
            setIsDeleting(false);
            setIsDeleteModalOpen(false);
            setTagToDelete(null);
        }
    };

    const onSubmit = async (data: AddTagFormData) => {
        if (!schoolSlug) return;
        setIsSubmitting(true);

        if (tagToEdit) {
            const response = await tagApi.updateTag(schoolSlug, tagToEdit.id, {
                name: data.name,
                description: data.description || ''
            });
            if (response.success && response.data) {
                setTags(prev => prev.map(t => t.id === tagToEdit.id ? response.data! : t));
                showToast('Tag updated successfully', 'success');
                setIsAddModalOpen(false);
                setTagToEdit(null);
                reset();
            } else {
                showToast(response.error || 'Failed to update tag', 'error');
            }
        } else {
            const response = await tagApi.createTag(schoolSlug, {
                name: data.name,
                description: data.description || ''
            });
            if (response.success && response.data) {
                setTags(prev => [...prev, response.data!]);
                showToast('Tag added successfully', 'success');
                setIsAddModalOpen(false);
                reset();
            } else {
                showToast(response.error || 'Failed to add tag', 'error');
            }
        }
        setIsSubmitting(false);
    };

    const columns: CustomTableColumn<Tag>[] = [
        {
            header: 'Tag Name',
            key: 'name',
            sortable: true,
            render: (tag) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontWeight: '500' }}>{tag.name}</span>
                </div>
            )
        },
        {
            header: 'Description',
            key: 'description',
            sortable: true,
            render: (tag) => <span>{tag.description || '-'}</span>
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '110px',
            width: '110px',
            render: (tag) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditClick(tag); }}
                        title="Edit Tag"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(tag); }}
                        title="Delete Tag"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Tags', icon: <TagIcon size={14} /> }
                ]}
            />

            <CustomCommonScreenHeader
                title="Tag Management"
                subtitle="Manage and organize tags for ticketing and reporting."
                icon={<TagIcon size={24} />}
                actionLabel="Add Tag"
                onActionClick={() => {
                    setTagToEdit(null);
                    reset({ name: '', description: '' });
                    setIsAddModalOpen(true);
                }}
            />

            {/* Responsive Filter / Search Bar */}
            <div className="table-filter-bar">
                <div className="table-search-box">
                    <Search size={16} className="table-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by tag name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button
                            className="table-search-clear"
                            onClick={() => setSearch('')}
                            title="Clear search"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            <div>
                <CustomTable
                    columns={columns}
                    data={tags}
                    isLoading={isLoading}
                    isCompact={true}
                    emptyMessage={debouncedSearch ? `No tags matching "${debouncedSearch}"` : "No tags found"}
                    emptyIcon={<TagIcon size={32} />}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={(colKey, order) => {
                        setSortBy(colKey);
                        setSortOrder(order);
                        setPage(1);
                    }}
                    pagination={{
                        currentPage: page,
                        totalPages: totalPages,
                        totalCount: totalCount,
                        pageSize: pageSize,
                        onPageChange: setPage,
                        hasPrevious: hasPrevious,
                        hasNext: hasNext,
                        isLoading: isLoading,
                        itemName: 'tags',
                    }}
                />
            </div>

            {/* Add/Edit Tag Modal */}
            <CustomModal
                isOpen={isAddModalOpen}
                onClose={() => {
                    setIsAddModalOpen(false);
                    setTagToEdit(null);
                    reset();
                }}
                title={tagToEdit ? "Edit Tag" : "Add New Tag"}
                size="small"
            >
                <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <CustomInput
                        label="Tag Name"
                        placeholder="Enter tag name (e.g. Urgent)"
                        error={errors.name?.message}
                        {...register('name')}
                        required
                    />

                    <CustomInput
                        label="Description"
                        placeholder="Enter tag description"
                        error={errors.description?.message}
                        {...register('description')}
                        required
                    />

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
                        <CustomButton
                            type="button"
                            variant="outline"
                            color="secondary"
                            onClick={() => {
                                setIsAddModalOpen(false);
                                setTagToEdit(null);
                                reset();
                            }}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </CustomButton>
                        <CustomButton
                            type="submit"
                            variant="filled"
                            color="primary"
                            loading={isSubmitting}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {tagToEdit ? <Pencil size={16} /> : <Plus size={16} />}
                                {tagToEdit ? "Save Changes" : "Create Tag"}
                            </div>
                        </CustomButton>
                    </div>
                </form>
            </CustomModal>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Tag"
                message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This action cannot be undone.`}
                confirmLabel={isDeleting ? "Deleting..." : "Delete"}
                variant="danger"
                isLoading={isDeleting}
            />
        </div>
    );
};
